import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaUsers } from "react-icons/fa6";
import "./dept-salary.css";

export default function DeptSalary() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [empData, setEmpData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [uniqueDepartments, setUniqueDepartments] = useState(["All"]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get("https://backend.ems.uk.hiradentallab.com/api/user/getAllUsers");
        setEmpData(response.data);
        setFilteredData(response.data);

        // Extract unique departments from the data
        const departments = [...new Set(response.data
          .map(emp => emp.department?.name)
          .filter(name => name !== undefined && name !== null)
        )];

        setUniqueDepartments(["All", ...departments]);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch employee data");
        setLoading(false);
        console.error("Error fetching employee data:", err);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!empData.length) return;

    let filtered = [];
    if (selectedDepartment === "All") {
      filtered = empData;
    } else {
      // Filter by selected department
      filtered = empData.filter(emp =>
        emp.department?.name === selectedDepartment
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [selectedDepartment, empData]);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalEntries = filteredData.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);

  const totalSalary = filteredData.reduce((sum, emp) => sum + (emp.salary || 0), 0);
  const totalBasicSalary = filteredData.reduce((sum, emp) => sum + (emp.basicSalary || 0), 0);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const half = Math.floor(maxVisiblePages / 2);
      if (currentPage <= half + 1) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage >= totalPages - half) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - half;
        endPage = currentPage + half;
      }
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  // Format currency with pound sign
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "-";
    return `£${amount.toLocaleString()}`;
  };

  return (
    <div className="container-fluid">
      <h1 className="department-title">
        <FaUsers /> Employees
      </h1>
      <hr />
      <div className="emps-payroll-content">
        {loading ? (
          <p>Loading employees...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : (
          <div>
            <div className="department-tabs">
              {uniqueDepartments.map((dept) => (
                <button
                  key={dept}
                  className={`dept-tab ${selectedDepartment === dept ? "active" : ""}`}
                  onClick={() => setSelectedDepartment(dept)}>
                  {dept}
                </button>
              ))}
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="table-header">
                  <tr className="emps-payroll-table-header">
                    <th>SR</th>
                    <th>Employee Name</th>
                    <th>Department</th>
                    <th>Salary</th>
                    <th>Basic Salary</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((emp, index) => (
                      <tr key={emp._id}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td>{emp.name || "-"}</td>
                        <td>{emp.department?.name || "-"}</td>
                        <td>{formatCurrency(emp.salary)}</td>
                        <td>{formatCurrency(emp.basicSalary)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">No employees found in this department</td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="table-footer">
                  <tr className="emps-payroll-table-footer">
                    <td>Total</td>
                    <td>-</td>
                    <td>{totalEntries}</td>
                    <td>{formatCurrency(totalSalary)}</td>
                    <td>{formatCurrency(totalBasicSalary)}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="entries-pagination-container">
                <p className="showing-entries">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to {" "}
                  {Math.min(currentPage * rowsPerPage, totalEntries)} of {" "}
                  {totalEntries} entries
                </p>
                <nav className="pagination-container">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}>
                        Previous
                      </button>
                    </li>
                    {pageRange().map((page) => (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}